import type {AuthProvider} from 'react-admin';
import {accessTokenService, authRepository} from '@/compositionRoot';
import {API_URL} from '@/config';
import {ADMIN_COMPANIES} from '@/domain/entities/User';
import {decodeJwt, isTokenExpired} from '@/domain/services/jwt';

const authProvider: AuthProvider = {
  // No se usa, ya que se está redirigiendo al login del front. Se deja como ejemplo de implementación
  login: async ({username, password}) => {
    const result = await authRepository.create({email: username, password});

    if ('access_token' in result) {
      await accessTokenService.set(result.access_token);
      return;
    }

    throw new Error(('message' in result ? result.message : undefined) || 'Login failed');
  },

  logout: async () => {
    const access_token = accessTokenService.get();
    if (access_token) {
      try {
        await fetch(`${API_URL}/v1/auth/jwt/logout`, {
          method: 'POST',
          headers: {Authorization: `Bearer ${access_token}`},
        });
      } catch {
        // Ignore logout errors
      }
    }
    await accessTokenService.remove();
    return Promise.resolve();
  },

  checkAuth: async () => {
    const access_token = await accessTokenService.get();
    if (!access_token) {
      return Promise.reject();
    }
    const decoded = decodeJwt(access_token);
    if (!decoded || isTokenExpired(decoded, 0)) {
      await accessTokenService.remove();
      return Promise.reject();
    }
  },

  checkError: async (error) => {
    const status = error?.status ?? error?.response?.status;
    if (status === 401 || status === 403) {
      await accessTokenService.remove();
      return Promise.reject();
    }
  },

  getIdentity: async () => {
    const user = await authRepository.me();
    return {
      id: user.uuid,
      fullName: `${user.username}`,
      avatar: undefined,
      organization: user.organization,
    };
  },

  canAccess: async () => {
    const user = await authRepository.me();
    return ADMIN_COMPANIES.includes(user.organization);
  },

  getPermissions: async () => {
    // No RBAC for now
    return undefined;
  },
};

export default authProvider;
